import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Instagram, Facebook, Linkedin, Play, Eye, TrendingUp, ArrowUpRight } from "lucide-react";
import useApi from "../hooks/useApi";
import { useBranch } from "../context/BranchContext";
import PostAnalytics from "./PostAnalytics";

const PLAT = { Instagram, Facebook, LinkedIn: Linkedin };
const FORMAT_COLOR = { Reel: "#678A16", Story: "#E5A93D", Feed: "#93C020" };
const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : n);

export default function RecentPosts() {
  const { branchId } = useBranch();
  const { data } = useApi(`/posts?branch_id=${branchId}`, [branchId]);
  const [active, setActive] = useState(null);
  const posts = data || [];

  return (
    <section className="mb-10">
      <div className="flex items-baseline gap-3 mb-4">
        <h2 className="text-xl font-bold tracking-tight text-ink">Recent posts</h2>
        <span className="text-sm text-ink-2">Tap any post for a full performance breakdown.</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {posts.map((p, i) => {
          const PlatIcon = PLAT[p.platform] || Instagram;
          const isVideo = p.format === "Reel";
          return (
            <motion.button key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              onClick={() => setActive(p)} data-testid={`post-card-${p.id}`}
              className="card card-hover overflow-hidden text-left flex flex-col">
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(21,21,21,.55))" }} />
                <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-white/95" style={{ color: FORMAT_COLOR[p.format] }}>
                  {isVideo && <Play size={10} fill={FORMAT_COLOR[p.format]} strokeWidth={0} />} {p.format}
                </span>
                <div className="absolute bottom-2.5 left-2.5 right-2.5 flex items-center gap-2 text-white">
                  <PlatIcon size={14} strokeWidth={1.75} />
                  <span className="text-sm font-semibold truncate">{p.title}</span>
                </div>
              </div>
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="grid place-items-center w-8 h-8 rounded-lg" style={{ background: "var(--grad-green-soft)" }}>
                    {isVideo ? <Play size={15} color="#5F8213" strokeWidth={2} /> : <Eye size={15} color="#5F8213" strokeWidth={2} />}
                  </span>
                  <div>
                    <div className="text-lg font-extrabold tracking-tighter text-ink leading-none">{fmt(p.top_metric)}</div>
                    <div className="text-[11px] text-ink-3 mt-0.5">{isVideo ? "views" : "impressions"}</div>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-xs font-bold" style={{ color: "#678A16" }}>
                  <TrendingUp size={13} strokeWidth={2} /> {p.engagement_rate}% <ArrowUpRight size={13} strokeWidth={2} />
                </span>
              </div>
            </motion.button>
          );
        })}
      </div>

      <AnimatePresence>
        {active && <PostAnalytics post={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
}
